import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, ArrowUpRight, X } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";
import SectionHeading from "./SectionHeading";

const ease = [0.16, 1, 0.3, 1];
const externalUrl = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);
const openExternal = (e, u) => { e.preventDefault(); const url = externalUrl(u); if (url) window.open(url, "_blank", "noopener,noreferrer"); };

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay, ease }}
    >
      {children}
    </motion.div>
  );
}

export default function Details() {
  const wedding = useContent();
  const [lightbox, setLightbox] = useState(null);
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  };

  return (
    <section id="detalles" className="bg-paper pt-24 sm:pt-36 pb-20 sm:pb-24">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <SectionHeading number="03" kicker="Lo que necesitas saber" title="Los Detalles" />

        <div className="grid lg:grid-cols-12 gap-x-16 gap-y-20 items-start">
          {/* Itinerary */}
          {wedding.showItinerary && (
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline text-[color:var(--muted)] mb-8">Itinerario</p>
            </Reveal>
            <div className="border-t border-black/10">
              {wedding.itinerary.map((it, i) => (
                <Reveal key={it.title} delay={i * 0.06}>
                  <div className="grid grid-cols-[80px_1fr] sm:grid-cols-[120px_1fr] gap-6 py-6 border-b border-black/10 items-baseline">
                    <span className="font-display text-2xl sm:text-3xl">{it.time}</span>
                    <div>
                      <h4 className="font-display text-xl sm:text-2xl">{it.title}</h4>
                      <p className="text-sm font-light text-[color:var(--muted)] mt-1">{it.detail}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          )}

          {/* Dress code */}
          <div className={`lg:col-span-5 ${wedding.showItinerary ? "lg:pt-14" : "lg:col-start-8 lg:row-start-1 lg:row-span-2"}`}>
            <Reveal>
              <div className="bg-ink text-white p-10 sm:p-12">
                <p className="overline text-white/50 mb-6">Código de Vestimenta</p>
                <h3 className="font-display text-4xl sm:text-5xl tracking-tight">{wedding.dressCode.label}</h3>
                <p className="font-display italic text-xl text-white/70 mt-2">{wedding.dressCode.sub}</p>
                <p className="mt-8 font-light text-white/70 leading-relaxed text-sm">
                  {wedding.dressCode.detail}
                </p>
                {(wedding.dressCode.images || []).some((u) => u) && (
                  <div className="mt-8 grid grid-cols-2 gap-4">
                    {(wedding.dressCode.images || []).map((src, i) =>
                      src ? (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightbox(src)}
                          className="group relative overflow-hidden aspect-[3/4] bg-white/10 cursor-zoom-in"
                          data-testid={`dresscode-thumb-${i}`}
                        >
                          <img
                            src={src}
                            alt={`Sugerencia de vestimenta ${i + 1}`}
                            className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                            loading="lazy"
                            data-testid={`dresscode-image-${i}`}
                          />
                          <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                        </button>
                      ) : null
                    )}
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          {/* Gifts */}
          <div className={`lg:col-span-7 ${wedding.showItinerary ? "" : "lg:col-start-1 lg:row-start-1"}`}>
            <Reveal>
              <p className="overline text-[color:var(--muted)] mb-6">Mesa de Regalos</p>
              <p className="font-light text-[color:var(--muted)] max-w-lg leading-relaxed">{wedding.gifts.intro}</p>
            </Reveal>

            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              <Reveal>
                <div className="border border-black/10 p-8">
                  <p className="overline text-[color:var(--muted)]">Transferencia</p>
                  <p className="font-display text-xl mt-4">{wedding.gifts.bank.bankName}</p>
                  <p className="text-sm font-light text-[color:var(--muted)] mt-1">{wedding.gifts.bank.holder}</p>
                  <button
                    onClick={() => copy(wedding.gifts.bank.clabe.replace(/\s/g, ""), "CLABE")}
                    className="group mt-5 flex items-center justify-between w-full border-t border-black/10 pt-4"
                    data-testid="copy-clabe"
                  >
                    <span className="text-left">
                      <span className="block text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)] mb-1">{wedding.gifts.bank.clabeLabel || "CLABE"}</span>
                      <span className="font-body tabular-nums tracking-wide">{wedding.gifts.bank.clabe}</span>
                    </span>
                    <Copy size={16} className="text-[color:var(--muted)] group-hover:text-ink transition-colors shrink-0 ml-3" />
                  </button>
                  {(wedding.gifts.accounts || []).map((acc, i) => (
                    acc.number ? (
                      <button
                        key={i}
                        onClick={() => copy(acc.number.replace(/\s/g, ""), acc.bank || "Cuenta")}
                        className="group mt-4 flex items-center justify-between w-full border-t border-black/10 pt-4"
                        data-testid={`copy-account-${i}`}
                      >
                        <span className="text-left">
                          <span className="block text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)] mb-1">{acc.bank}</span>
                          <span className="font-body tabular-nums tracking-wide">{acc.number}</span>
                        </span>
                        <Copy size={16} className="text-[color:var(--muted)] group-hover:text-ink transition-colors shrink-0 ml-3" />
                      </button>
                    ) : null
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.08}>
                <div className="border border-black/10 p-8 h-full">
                  <p className="overline text-[color:var(--muted)]">Tiendas</p>
                  <div className="mt-4 space-y-4">
                    {wedding.gifts.registries.map((r) => (
                      <a
                        key={r.store}
                        href={externalUrl(r.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => openExternal(e, r.url)}
                        className="group flex items-center justify-between border-b border-black/10 pb-3"
                        data-testid={`registry-${r.store.toLowerCase()}`}
                      >
                        <span>
                          <span className="font-display text-lg">{r.store}</span>
                          <span className="block text-xs font-light text-[color:var(--muted)]">{r.code}</span>
                        </span>
                        <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Notes */}
          <div className={`${wedding.showItinerary ? "lg:col-span-5 lg:pt-2" : "lg:col-span-7 lg:col-start-1 lg:row-start-2"}`}>
            <Reveal>
              <p className="overline text-[color:var(--muted)] mb-6">Notas Extra</p>
            </Reveal>
            <div className="space-y-6">
              {wedding.notes.map((n, i) => (
                <Reveal key={n.title} delay={i * 0.06}>
                  <div>
                    <h4 className="font-display text-lg">{n.title}</h4>
                    <p className="text-sm font-light text-[color:var(--muted)] mt-1 leading-relaxed">{n.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            data-testid="dresscode-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
              aria-label="Cerrar"
              data-testid="dresscode-lightbox-close"
            >
              <X size={28} strokeWidth={1} />
            </button>
            <div className="absolute top-8 left-8">
              <span className="overline text-white/50">Sugerencia de vestimenta</span>
            </div>
            <motion.img
              src={lightbox}
              alt="Sugerencia de vestimenta"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.5, ease }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
